<script setup lang="ts">
import type { AppToastOptions } from '#core/composables/useAppToast'
import type { DirectUploadResponse, UploadResponse } from '#core/types/cloudflare'
import { Button } from '@forgd/ui-kit/button'
import { CameraIcon } from '@heroicons/vue/24/outline'
import { useMutation } from '@pinia/colada'
import { useDropZone } from '@vueuse/core'

const props = defineProps<{
  image?: string
  disabled?: boolean
  layout?: 'fill'
  noProject?: boolean
  disableAutoUpload?: boolean
  error?: boolean
}>()
const emit = defineEmits<{ (event: 'update:image', value: string): void }>()

const MAX_BYTES = 5 * 1024 * 1024
const MIN_DIM = 150

const auth = useAuth()
const supabase = useSupabaseClient()
const toast = useAppToast()
const logger = useDevLogger('project-image-upload')

const {
  mutateAsync: updateProjectImage,
} = useMutation(buildApiMutation('/projects/{projectId}/update-image', {
  method: 'patch',
}))

const image = ref(props.image || auth.project?.image || null)
const previewImage = ref<File | undefined>()
const dropZoneRef = ref<HTMLDivElement>()
const logo = ref<HTMLInputElement>()
const busy = ref(false)

const disableAutoUpload = ref(props.disableAutoUpload)
watch(() => props.disableAutoUpload, (newValue) => {
  disableAutoUpload.value = newValue
}, { immediate: true })

defineExpose({ busy, previewImage, reset, uploadImage })

const dataTypes = [
  'image/jpeg',
  'image/png',
  'image/svg+xml',
]

const toastUploadError: AppToastOptions = {
  id: 'upload-error',
  title: 'Image upload failed',
}

const { isOverDropZone } = useDropZone(dropZoneRef, {
  dataTypes,
  multiple: false,
  preventDefaultForUnhandled: false,
  onDrop: (files) => {
    const file = files?.[0]
    if (file && disableAutoUpload.value) {
      inputImage(file)
    }
    else if (file) {
      uploadImage(file)
    }
  },
})

function extractFile(fileDrop?: File | Event | string) {
  let file: File | null = null

  if (fileDrop instanceof File) {
    file = fileDrop
  }
  else if (
    fileDrop instanceof Event &&
    fileDrop.target instanceof HTMLInputElement &&
    fileDrop.target.files !== null
  ) {
    file = fileDrop.target.files[0] ?? null
    fileDrop.target.value = ''
  }
  else if (logo.value?.files?.length) {
    file = logo.value.files[0] ?? null
    logo.value.value = ''
  }

  return file
}

async function validateImage(file: File) {
  if (file.size > MAX_BYTES) {
    return {
      ...toastUploadError,
      title: 'Image size too large',
      description: 'Please upload an image that is less than 5 MB in size.',
    }
  }

  const imageElement = new Image()
  imageElement.src = URL.createObjectURL(file)

  await new Promise((resolve, reject) => {
    imageElement.onload = () => resolve(true)
    imageElement.onerror = () => reject(new Error('Image failed to load'))
  })

  if (
    (imageElement.width < MIN_DIM || imageElement.height < MIN_DIM) &&
    file.type !== 'image/svg+xml'
  ) {
    return {
      ...toastUploadError,
      title: 'Image dimensions too small',
      description: `Please upload an image that is at least ${MIN_DIM} x ${MIN_DIM} pixels in size.`,
    }
  }

  if (imageElement.width !== imageElement.height) {
    return {
      ...toastUploadError,
      title: 'Image Upload Failed: The image must be in a square format.',
      description: 'Please upload an image with equal width and height dimensions to proceed.',
    }
  }

  return null
}

function handleFileBrowse(event?: Event) {
  if (props.disabled) {
    return
  }

  if (event) {
    logo.value?.click()
  }
  else {
    if (!logo.value?.files?.length || logo.value.files === null) {
      logger.error('No file selected')
      return
    }
    const file = logo.value.files[0]
    if (!file) {
      logger.error('No file selected')
      return
    }
    uploadImage(file)
  }
}

function reset(previousImage: string | null) {
  if (!previousImage) {
    return
  }

  image.value = previousImage
  emit('update:image', previousImage)
}

async function inputImage(fileDrop?: File | Event) {
  const file = extractFile(fileDrop)
  if (!file) {
    return
  }

  const validationResult = await validateImage(file)
  if (validationResult) {
    toast.error(validationResult)
    return
  }

  if (disableAutoUpload.value) {
    previewImage.value = file
    image.value = URL.createObjectURL(file)
    emit('update:image', '')
  }
  else {
    uploadImage(file)
  }
}

async function uploadImage(fileDrop?: File | Event) {
  // INFO: Image is still processing, wait for the first process to finish
  if (busy.value) {
    return
  }

  // INFO: Allow upload even if no project yet e.g. onboarding
  // TODO: !auth.ptoject and !props.noProject seems redundant check if both are needed
  if (
    !auth.project &&
    !props.noProject &&
    busy.value
  ) {
    return
  }

  busy.value = true

  const file = extractFile(fileDrop)
  if (!file) {
    busy.value = false
    return
  }

  const validationResult = await validateImage(file)
  if (validationResult) {
    toast.error(validationResult)
    busy.value = false
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  const reader = new FileReader()
  reader.onload = (event) => {
    image.value = event.target?.result as string
  }
  reader.readAsDataURL(file)

  let uploadResult: UploadResponse | null = null

  try {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session?.access_token) {
      toast.error(toastUploadError)
      image.value = null
      busy.value = false
      return
    }

    const { success, result } = await $fetch<DirectUploadResponse>('/image', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    })

    if (!success) {
      toast.error(toastUploadError)
      image.value = null
      busy.value = false
      return
    }

    uploadResult = await $fetch<UploadResponse>(
      result.uploadURL,
      {
        method: 'POST',
        body: formData,
      },
    )
  }
  catch {
    toast.error(toastUploadError)
    image.value = null
    busy.value = false
    return
  }

  if (!uploadResult?.success) {
    toast.error(toastUploadError)
    image.value = null
    busy.value = false
    return
  }

  const uploadedImage = uploadResult.result.variants[0]
  if (!uploadedImage) {
    toast.error(toastUploadError)
    image.value = null
    busy.value = false
    return
  }

  // INFO: Only update when project exists
  if (auth.project && !props.noProject) {
    try {
      await updateProjectImage({
        path: { projectId: auth.project.id },
        body: { image: uploadedImage },
      })
    }
    catch (error) {
      toast.error(toToastError(error))
      busy.value = false
      return
    }
  }

  toast.success({
    id: 'upload-success',
    title: 'Image upload successful',
  })

  image.value = uploadedImage
  emit('update:image', uploadedImage)

  // INFO: Forces projects to be refreshed
  await auth.refresh({ from: 'ProjectImageUpload:uploadImage' })

  busy.value = false
  return { success: true }
}

watch(() => props.image, (newValue) => {
  image.value = newValue ?? auth.project?.image ?? null
})

onMounted(() => {
  image.value = props.image ?? null
})

function toToastError(error: unknown): AppToastOptions {
  return error instanceof Error
    ? { ...toastUploadError, description: error.message }
    : toastUploadError
}
</script>

<template>
  <div
    ref="dropZoneRef"
    data-ui="ProjectImageUpload"
    class="rounded-lg border border-dashed p-5 text-center flex flex-col justify-center items-center gap-2"
    data-testid="uiProjectImageUpload"
    v-bind="$attrs"
    :class="{
      'w-full': props.layout === 'fill',
      'bg-neutral-600/10': isOverDropZone,
      'border-red-600': error,
      'border-default': !error,
    }"
  >
    <input
      id="project-image"
      ref="logo"
      type="file"
      :accept="dataTypes.join(',')"
      class="hidden"
      :disabled="disabled || busy"
      data-testid="uiProjectImageUploadInput"
      @input="inputImage"
    >

    <div
      v-if="image" class="mx-auto rounded-full flex flex-col justify-center items-center gap-2"
    >
      <img
        id="project-image-uploaded"
        :src="image"
        class="rounded-full size-8"
        data-testid="uiProjectImageUploadImage"
      >
    </div>
    <div v-else class="flex justify-center items-center rounded-full border border-default size-8 bg-neutral-300 text-neutral-700">
      <CameraIcon class="size-5" aria-hidden="true" />
    </div>
    <div v-if="$slots.description" class="text-sm text-primal">
      <slot name="description" />
    </div>

    <Button
      variant="outline"
      size="sm"
      :loading="busy"
      :disabled="disabled"
      data-testid="uiProjectImageUploadLabel"
      @click="handleFileBrowse"
    >
      <slot name="label">
        Select image file
      </slot>
    </Button>

    <div class="text-muted text-xs font-regular">
      <p>Use .JPG or .PNG files. 5 MB max.</p>
      <p>Use a square image, not smaller than 150x150px.</p>
    </div>
  </div>
</template>
